/**
 * 料金ページ「機能の詳細比較」テーブルの行定義。
 *
 * 数値上限は backend/src/common/usage_limits.py の PLAN_LIMITS が SSOT。
 * 値を変更したら両方を必ず同期させること（共同編集者の上限は API の maxCollaborators で受け取るのでフロントに定数を持たない）。
 *
 * 値の型: true = 対応（チェックマーク表示）/ false = 非対応（—表示）/ string = 上限などの表示文字列
 */
export interface PlanComparisonRow {
  feature: string;
  /** 補足説明。PC では機能名に（）付きで続け、スマホでは2行目に小さく表示する */
  note?: string;
  free: string | boolean;
  basic: string | boolean;
  premium: string | boolean;
}

/**
 * AI アシスタントの行。AI アシスタントのパネルでも「上のプランなら月◯回ほど」と出すので名前を付けて共有する
 * （components/chat/ChatUsageNotice.tsx）。
 */
export const AI_ASSISTANT_ROW: PlanComparisonRow = {
  feature: 'AIアシスタント',
  // 上限は回数ではなく、AIの利用量（トークン数を料金で重み付けした量）の月間予算
  // （backend/src/common/usage_limits.py）。回数は、用途を選んで 1 回使ったときの費用を
  // $0.0012 と高めに見積もった目安（2026-10-18 の評価で 1 回 $0.0002〜0.0011。
  // backend/scripts/chat_eval/）。予算 ÷ 0.0012 を切り下げて丸めた（公開の初期は控えめ）
  note: '回数は目安。長い会話ほど少なくなります',
  free: '月5回ほど',
  basic: '月60回ほど',
  premium: '月200回ほど',
};

// 比較表にはプラン差のある機能だけを載せる（全プラン共通の機能は COMMON_FEATURES へ）
export const PLAN_COMPARISON_ROWS: PlanComparisonRow[] = [
  {
    feature: '企画作成',
    note: '同時に主催できる数。下書き・中止・終了も含み、削除すると枠が戻ります',
    free: '3件',
    basic: '100件',
    premium: '10,000件',
  },
  { feature: 'AI企画生成', free: '月5回', basic: '月35回', premium: '月100回' },
  AI_ASSISTANT_ROW,
  {
    feature: '参加者数',
    note: '1イベントにつき',
    free: '10人',
    basic: '50人',
    premium: '200人',
  },
  {
    feature: '共同編集者',
    note: '主催者と一緒に企画を編集',
    free: '1人',
    basic: '5人',
    premium: '20人',
  },
  {
    feature: 'URLからイベントを作成',
    note: '告知ページのURLを貼り付けて取り込み',
    free: '1日10回',
    basic: '1日30回',
    premium: '1日100回',
  },
  {
    feature: 'URL取り込みのAI読み取り',
    note: '足りない項目をAIが本文から読み取り',
    free: false,
    basic: '1日10回',
    premium: '1日30回',
  },
  {
    feature: 'イベントページのデザイン',
    note: 'カラーテーマ・ヘッダー模様',
    free: false,
    basic: true,
    premium: true,
  },
  { feature: 'リマインダー通知（LINE）', free: false, basic: false, premium: true },
  { feature: '優先サポート', free: false, basic: false, premium: true },
];

export interface CommonFeature {
  label: string;
  /** 補足説明。ラベルの下に小さく表示する */
  note?: string;
}

// 全プラン共通で使える機能（比較表の下に一覧で表示する）
export const COMMON_FEATURES: CommonFeature[] = [
  { label: '招待リンク共有' },
  { label: 'ゲスト参加', note: 'ログイン不要' },
  { label: 'アンケート・候補日投票' },
  { label: '最寄り駅アンケート', note: '回答から「みんなの中間地点」で集合駅を探せる' },
  { label: 'イベント連携ツール', note: '割り勘・やることリスト・買い出しリストなど' },
  { label: '開催レポート', note: '開催後のまとめを画像で保存・共有' },
  { label: 'イベントの履歴', note: '参加登録・投票・コメントの記録を時系列で確認' },
  { label: '活動ログ', note: '過去のイベントを振り返り' },
  { label: 'リマインダー通知（メール）' },
  { label: 'お知らせメール', note: '変更・中止・お礼' },
  { label: 'アプリ内通知', note: '参加登録や回答を通知ベルで確認' },
];
